import { useState } from 'react';
import { useSeoMeta, useHead } from '@unhead/react';
import {Phone, MapPin, Globe, Mail, CheckCircle, AlertCircle, Loader2} from 'lucide-react';
import { SiteShell } from '@/components/SiteShell';
import { PageHero } from '@/components/PageHero';
import { normalizeCanonicalUrl } from '@/lib/seo';
import contactBg from '../../images/VIP House Movers Removal Van.jpg';
import depotImage from '../../images/VIP House Movers Home Removal Van.jpg';
import teamLoading from '../../images/VIP House Movers Team Loading Removal Van.jpg';
import teamPacking from '../../images/VIP House Movers Team Packing.jpg';
import teamCustomer from '../../images/VIP House Movers Team with happy customer.jpg';
import teamWorking from '../../images/VIP House Movers Removal Team working.jpg';
import gardenSeatRemoval from '../../images/vip-house-movers-garden-seat-removal.jpeg';

// Web3Forms — free, no backend needed. Get your free access key at https://web3forms.com
// Replace the key below with your own from web3forms.com (sign up with info@viphousemovers.com)
const WEB3FORMS_KEY = 'ff8dcc3a-1d86-4e87-8503-c40947d75565';

export default function Contact() {
  useSeoMeta({
    title: 'Contact VIP House Movers | Local Removal Company Wolverhampton',
    description: 'Contact VIP House Movers in Wolverhampton for house removals, man and van service, packing support and local removals quotes. Call 0121 752 7107 or email info@viphousemovers.com.',
    ogTitle: 'Contact VIP House Movers | 0121 752 7107',
    ogDescription: 'Get in touch with VIP House Movers. Call 0121 752 7107 or email info@viphousemovers.com for a free removals quote.',
    ogImage: 'https://www.viphousemovers.com/og-home.jpg',
    ogUrl: 'https://www.viphousemovers.com/contact',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: 'Contact VIP House Movers | 0121 752 7107',
    twitterDescription: 'Call 0121 752 7107 or email info@viphousemovers.com for a free removals quote.',
    twitterImage: 'https://www.viphousemovers.com/og-home.jpg',
  });
  const canonicalHref = normalizeCanonicalUrl('https://www.viphousemovers.com/contact');

  useHead({
    link: [{ rel: 'canonical', href: canonicalHref }],
    meta: [
      {
        name: 'keywords',
        content: 'contact VIP House Movers, removals quote Wolverhampton, removals enquiry, moving company phone number, local removals contact, house removals quote, contact removal experts',
      },
    ],
    script: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: 'https://www.viphousemovers.com',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Contact',
              item: 'https://www.viphousemovers.com/contact',
            },
          ],
        }),
      },
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'LocalBusiness',
          name: 'VIP House Movers',
          url: 'https://www.viphousemovers.com',
          telephone: '+44-121-752-7107',
          email: 'info@viphousemovers.com',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Wolverhampton',
            addressRegion: 'West Midlands',
            addressCountry: 'GB',
          },
          areaServed: [
            'Wolverhampton',
            'Birmingham',
            'Walsall',
            'Dudley',
            'Staffordshire',
            'Cannock',
            'Telford',
            'Lichfield',
            'Tamworth',
            'Sutton Coldfield',
            'West Bromwich',
          ],
          contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'Customer Service',
            telephone: '+44-121-752-7107',
            email: 'info@viphousemovers.com',
          },
        }),
      },
    ],
  });

  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const inputClass = (field: string) =>
    `w-full px-4 py-3 border rounded-lg outline-none transition-colors focus:ring-2 focus:ring-[#D4AF37]/40 focus:border-[#D4AF37] ${
      errors[field] ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'
    }`;

  function validate() {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = 'Please enter your name.';
    if (!form.email.trim()) e.email = 'Please enter your email address.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Please enter a valid email address.';
    if (!form.message.trim()) e.message = 'Please enter your message.';
    return e;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setStatus('loading');
    setErrorMsg('');

    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          subject: `New Contact Enquiry from ${form.name} — VIP House Movers`,
          from_name: 'VIP House Movers Website',
          replyto: form.email,
          name: form.name,
          email: form.email,
          phone: form.phone || 'Not provided',
          message: form.message,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Something went wrong.');
      setStatus('success');
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err: unknown) {
      setStatus('error');
      setErrorMsg(
        err instanceof Error
          ? err.message
          : 'Unable to send your message. Please call us on 0121 752 7107.'
      );
    }
  }

  return (
    <SiteShell activePath="/contact" fixedNav>
      <PageHero
        eyebrow="Contact VIP House Movers"
        title="We're here to answer all your moving queries"
        description="Reach out to our Wolverhampton-based team for tailored advice, fast quotes, and premium moving support across the Midlands and beyond."
        align="center"
        primaryLabel="Call 0121 752 7107"
        primaryTo="/contact"
        secondaryLabel="Request a Free Quote"
        secondaryTo="/quote"
        backgroundImage={contactBg}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12">

          {/* ── Contact Form ─────────────────────────────────────────── */}
          <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
            <h2 className="text-2xl font-bold mb-2 text-gray-900">Get in Touch</h2>
            <p className="text-gray-500 text-sm mb-6">Fill in the form below and we'll get back to you shortly. You'll receive a confirmation email instantly.</p>

            {status === 'success' ? (
              <div className="flex flex-col items-center justify-center py-12 text-center gap-4">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Message Sent!</h3>
                <p className="text-gray-600 max-w-sm">
                  Thank you for contacting VIP House Movers. We've sent a confirmation to your email and will be in touch very soon.
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-2 text-sm text-[#D4AF37] font-semibold hover:underline"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className={inputClass('name')}
                    placeholder="Your full name"
                    autoComplete="name"
                  />
                  {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    className={inputClass('email')}
                    placeholder="your@email.com"
                    autoComplete="email"
                  />
                  {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
                </div>

                {/* Phone (optional) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    className={inputClass('phone')}
                    placeholder="07700 900000"
                    autoComplete="tel"
                  />
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    rows={5}
                    value={form.message}
                    onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                    className={inputClass('message')}
                    placeholder="How can we help? Tell us about your move..."
                  />
                  {errors.message && <p className="mt-1 text-xs text-red-500">{errors.message}</p>}
                </div>

                {/* API Error */}
                {status === 'error' && (
                  <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{errorMsg}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full flex items-center justify-center gap-2 bg-[#D4AF37] text-gray-900 font-bold py-3.5 rounded-xl hover:bg-yellow-500 transition-all shadow-md hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === 'loading' ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="w-5 h-5" />
                      Send Message
                    </>
                  )}
                </button>
                <p className="text-center text-xs text-gray-400">
                  Your message is sent directly to info@viphousemovers.com. No data is stored.
                </p>
              </form>
            )}
          </div>

          {/* ── Contact Info ─────────────────────────────────────────── */}
          <div className="space-y-8">
            <div className="overflow-hidden rounded-2xl shadow-sm border border-gray-100">
              <img
                src={depotImage}
                alt="VIP House Movers depot and storage facility on Vulcan Road, Wolverhampton"
                width={1200}
                height={675}
                loading="lazy"
                decoding="async"
                className="w-full h-64 object-cover"
              />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-6 text-gray-900">Contact Information</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 rounded-xl bg-white border border-gray-100 shadow-sm">
                  <MapPin className="w-6 h-6 text-[#D4AF37] mt-1 shrink-0" />
                  <div>
                    <h4 className="font-bold text-gray-900">Address</h4>
                    <p className="text-gray-600">Self Storage, Wolverhampton<br />Vulcan Rd, Wolverhampton<br />Bilston WV14 7LF</p>
                  </div>
                </div>
                <a href="tel:01217527107" className="flex items-center gap-4 p-4 rounded-xl bg-white border border-gray-100 shadow-sm hover:border-[#D4AF37]/40 hover:shadow-md transition-all group">
                  <Phone className="w-6 h-6 text-[#D4AF37] shrink-0" />
                  <div>
                    <h4 className="font-bold text-gray-900">Phone</h4>
                    <p className="text-gray-600 group-hover:text-[#D4AF37] transition-colors">0121 752 7107</p>
                  </div>
                </a>
                <a href="mailto:info@viphousemovers.com" className="flex items-center gap-4 p-4 rounded-xl bg-white border border-gray-100 shadow-sm hover:border-[#D4AF37]/40 hover:shadow-md transition-all group">
                  <Mail className="w-6 h-6 text-[#D4AF37] shrink-0" />
                  <div>
                    <h4 className="font-bold text-gray-900">Email</h4>
                    <p className="text-gray-600 group-hover:text-[#D4AF37] transition-colors">info@viphousemovers.com</p>
                  </div>
                </a>
                <div className="flex items-center gap-4 p-4 rounded-xl bg-white border border-gray-100 shadow-sm">
                  <Globe className="w-6 h-6 text-[#D4AF37] shrink-0" />
                  <div>
                    <h4 className="font-bold text-gray-900">Website</h4>
                    <p className="text-gray-600">viphousemovers.com</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Visual reassurance strip */}
        <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { src: teamLoading, alt: 'Friendly VIP House Movers team loading a removal van in Wolverhampton' },
            { src: teamPacking, alt: 'Family receiving keys after a smooth house move with VIP House Movers' },
            { src: teamCustomer, alt: 'VIP House Movers branded removal vehicle ready for a Midlands relocation' },
            { src: teamWorking, alt: 'Professional packers from VIP House Movers protecting fragile items' },
            { src: gardenSeatRemoval, alt: 'VIP House Movers outdoor furniture removal and garden prep service' },
          ].map((img) => (
            <div key={img.src} className="relative aspect-square overflow-hidden rounded-2xl shadow-sm group">
              <img
                src={img.src}
                alt={img.alt}
                width={600}
                height={600}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
          ))}
        </div>
      </div>
    </SiteShell>
  );
}

